define((require) => {
    "use strict";

    const _             = require("underscore");
    const Class         = require("shared/util/Class");
    const Preconditions = require("shared/util/Preconditions");

    /**
     * Describes a range between 2 breakpoints. <p>
     *
     * The "from" breakpoint is always included in the range, the "to" breakpoint is not included in the range.
     * That is, each range in mathematical terms is like [from, to[.
     *
     * @class BreakpointRange
     */
    return Class.extend("BreakpointRange", {
        // TODO: consider simply storing two numbers instead of Breakpoint models.

        /**
         * @private @type {Breakpoint}
         * The from breakpoint of this range, inclusive; never falsey.
         */
        _from: null,
        /**
         * @private @type {Breakpoint}
         * The to breakpoint of this range, exclusive; never falsey.
         */
        _to: null,

        /**
         * Constructor.
         *
         * @param {Breakpoint} from The starting breakpoint; cannot be falsey.
         * @param {Breakpoint} to   The ending breakpoint; cannot be falsey.
         */
        constructor(from, to) {
            Preconditions.checkArguments(from, to);
            Preconditions.checkState(from.get("threshold") < to.get("threshold"));

            this._from = from;
            this._to = to;
        },

        /**
         * Returns the (to) breakpoint representing this range, always defined as the <tt>to</tt> breakpoint since
         * it is always exclusive - once said breakpoint is crossed, a new range is known to take effect, with
         * the same breakpoint threshold, but inclusive. It also ensures that we do not report <tt>Breakpoint.BEGIN</tt>
         * since that can never be crossed.
         *
         * @return {Breakpoint} The (to) breakpoint for this range; never falsey.
         */
        breakpoint() {
            return this._to;
        },

        /**
         * Whether the specified threshold is contained within *this* range. <p>
         *
         * The from breakpoint of this range is always inclusive, the to breakpoint always exclusive.
         *
         * @return {Boolean} <tt>true</tt> if the threshold is contained within *this* range, <tt>false</tt> if not.
         */
        contains(threshold) {
            return (threshold >= this._from.get("threshold")) && (threshold < this._to.get("threshold"));
        },

        /**
         * Returns a user-friendly string describing this range like (0, 200) or (700, oo) for the last range. In
         * this representation, both from and to points are inclusive.
         *
         * @return {String} The range string; never falsey.
         */
        toString() {
            let to = this._to.get("threshold");
            if (_.isNumber(to) && (to !== Number.POSITIVE_INFINITY)) {
                to -= 1;
            }
            return `(${this._from.get("threshold")}, ${to})`;
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/breakpoint/BreakpointRange.js